/**
 * useChatKitToken - Token Management with Caching and Auto-Refresh
 * 
 * Provides ephemeral token management for ChatKit:
 * - Caches token until expiry - 15 seconds buffer
 * - Deduplicates concurrent requests (single in-flight promise)
 * - Auto-refreshes on 401 (one silent retry)
 * - Fail-closed: throws on persistent errors
 * 
 * @see https://platform.openai.com/docs/guides/agents-sdk
 */

import { ensureSupabaseClientConfig } from '@/lib/env/runtime-env';
import { devLog } from '@/lib/logger';
import { useCallback, useRef } from 'react';

interface TokenCache {
  token: string;
  expiresAt: number; // Unix timestamp in seconds
}

interface TokenResponse {
  ok: boolean;
  data?: {
    token: string;
    expires_at?: string;
    agent_id?: string;
    session_id?: string;
  };
  error?: unknown;
}

// Singleton cache and in-flight promise
let tokenCache: TokenCache | null = null;
let inFlightPromise: Promise<string> | null = null;

const TOKEN_BUFFER_SECONDS = 15; // Refresh 15 seconds before expiry
const DEFAULT_TTL_SECONDS = 300; // 5 minutes default

/**
 * Parse JWT to extract expiration time
 * Returns null if parsing fails
 */
function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    if (typeof payload.exp === 'number') {
      return payload.exp;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Generate a trace ID for request correlation
 */
function generateTraceId(): string {
  return `trace_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

interface FetchTokenOptions {
  path?: string;
  isPublic?: boolean;
  traceId?: string;
  forceRefresh?: boolean;
}

/**
 * Fetch a new token from the edge function
 */
async function fetchToken(options: FetchTokenOptions = {}): Promise<string> {
  const {
    path = typeof window !== 'undefined' ? window.location.pathname : '/',
    isPublic = false,
    traceId = generateTraceId(),
    forceRefresh = false,
  } = options;

  const config = ensureSupabaseClientConfig();
  const supabaseUrl = config.url;
  const supabaseAnonKey = config.anonKey;

  // Get auth token if available (for authenticated requests)
  // Use existing browser client to avoid multiple GoTrueClient instances
  let authToken: string | null = null;
  if (!isPublic) {
    try {
      if (typeof window !== 'undefined') {
        const { supabaseBrowser } = await import('@/lib/supabase-browser');
        const { data: { session } } = await supabaseBrowser.auth.getSession();
        authToken = session?.access_token ?? null;
      } else {
        const { createClient } = await import('@supabase/supabase-js');
        const supabase = createClient(supabaseUrl, supabaseAnonKey);
        const { data: { session } } = await supabase.auth.getSession();
        authToken = session?.access_token ?? null;
      }
    } catch {
      // Ignore - will proceed without auth
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-trace-id': traceId,
  };

  if (supabaseAnonKey) {
    headers['apikey'] = supabaseAnonKey;
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Note: We use 'omit' for credentials because we pass the JWT in Authorization header
  // Using 'include' would require the server to return a specific origin (not '*') in CORS headers
  // eslint-disable-next-line internal/no-direct-edge-fetch -- Special case: ChatKit token generation endpoint
  const response = await fetch(`${supabaseUrl}/functions/v1/assistant-chatkit-token`, {
    method: 'POST',
    headers,
    credentials: 'omit',
    body: JSON.stringify({
      intent: isPublic ? 'assistant.intent.chatkit.public' : 'assistant.intent.chatkit.embed',
      metadata: {
        surface: isPublic ? 'public.landing' : 'app.dashboard',
        path,
        public: isPublic,
        trace_id: traceId,
        force_refresh: forceRefresh,
      },
    }),
  });

  // Handle rate limiting
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 5000;
    devLog(`[useChatKitToken] Rate limited. Retry after ${waitTime}ms`);
    throw new Error(`Rate limited. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('[useChatKitToken] ❌ Token fetch failed:', response.status, errorText);
    throw new Error(`Token fetch failed: ${response.status}`);
  }

  const data: TokenResponse = await response.json();

  if (!data.ok || !data.data?.token) {
    console.error('[useChatKitToken] ❌ Invalid token response:', data);
    throw new Error('Invalid token response from server');
  }

  const token = data.data.token;

  // Determine expiration time
  let expiresAt: number;
  if (data.data.expires_at) {
    expiresAt = Math.floor(new Date(data.data.expires_at).getTime() / 1000);
  } else {
    // Try to parse from JWT, or use default TTL
    const jwtExp = parseJwtExp(token);
    expiresAt = jwtExp ?? Math.floor(Date.now() / 1000) + DEFAULT_TTL_SECONDS;
  }

  // Update cache
  tokenCache = { token, expiresAt };

  if (import.meta.env.DEV) {
    devLog('[useChatKitToken] ✅ Token cached', {
      traceId,
      expiresIn: expiresAt - Math.floor(Date.now() / 1000),
      tokenLength: token.length,
    });
  }

  return token;
}

/**
 * Get a valid token, using cache if available
 * Single in-flight promise prevents duplicate requests
 */
async function getToken(options: FetchTokenOptions = {}): Promise<string> {
  const now = Math.floor(Date.now() / 1000);

  // Return cached token if still valid (with buffer)
  if (tokenCache && tokenCache.expiresAt - TOKEN_BUFFER_SECONDS > now && !options.forceRefresh) {
    if (import.meta.env.DEV) {
      devLog('[useChatKitToken] Using cached token', {
        expiresIn: tokenCache.expiresAt - now,
      });
    }
    return tokenCache.token;
  }

  // Deduplicate concurrent requests
  if (!inFlightPromise) {
    inFlightPromise = fetchToken(options)
      .finally(() => {
        inFlightPromise = null;
      });
  }

  return inFlightPromise;
}

/**
 * Clear the token cache
 * Useful after logout or when forcing a refresh
 */
export function clearTokenCache(): void {
  tokenCache = null;
  inFlightPromise = null;
  devLog('[useChatKitToken] Cache cleared');
}

/**
 * Check if token is currently cached and valid
 */
export function hasValidToken(): boolean {
  if (!tokenCache) return false;
  const now = Math.floor(Date.now() / 1000);
  return tokenCache.expiresAt - TOKEN_BUFFER_SECONDS > now;
}

interface UseChatKitTokenOptions {
  /** Current page path for telemetry */
  path?: string;
  /** Whether this is a public (unauthenticated) request */
  isPublic?: boolean;
  /** Callback when token is successfully fetched */
  onTokenFetched?: (token: string) => void;
  /** Callback when token fetch fails */
  onTokenError?: (error: Error) => void;
}

/**
 * Hook that provides a getClientSecret function for ChatKit
 * 
 * @example
 * ```tsx
 * function ChatKitComponent() {
 *   const { getClientSecret, clearCache } = useChatKitToken({ isPublic: true });
 *   
 *   const chatkit = useChatKit({
 *     api: { getClientSecret },
 *     // ... other options
 *   });
 * }
 * ```
 */
export function useChatKitToken(options: UseChatKitTokenOptions = {}) {
  const {
    path,
    isPublic = false,
    onTokenFetched,
    onTokenError,
  } = options;

  const retryCountRef = useRef(0);
  const MAX_RETRIES = 1; // One silent retry on 401

  /**
   * getClientSecret - Function compatible with ChatKit's api.getClientSecret
   * 
   * @param currentSecret - The current secret if refreshing
   * @returns Promise resolving to a new token
   */
  const getClientSecret = useCallback(
    async (currentSecret: string | null): Promise<string> => {
      const traceId = generateTraceId();

      if (import.meta.env.DEV) {
        devLog('[useChatKitToken] getClientSecret called', {
          hasCurrentSecret: !!currentSecret,
          traceId,
          path,
          isPublic,
        });
      }

      try {
        // If we have a current secret and it's being refreshed, try to get new one
        const forceRefresh = !!currentSecret;

        const token = await getToken({
          path,
          isPublic,
          traceId,
          forceRefresh,
        });

        // Reset retry count on success
        retryCountRef.current = 0;
        onTokenFetched?.(token);

        return token;
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));

        // On 401/token error, try one silent refresh
        if (retryCountRef.current < MAX_RETRIES &&
          (errorObj.message.includes('401') || errorObj.message.includes('Token'))) {
          retryCountRef.current++;
          devLog('[useChatKitToken] ⚠️ Token error, retrying...', { traceId });

          // Clear cache and retry
          clearTokenCache();
          return getToken({ path, isPublic, traceId, forceRefresh: true });
        }

        console.error('[useChatKitToken] ❌ Token fetch failed after retries', {
          error: errorObj.message,
          traceId,
          retryCount: retryCountRef.current,
        });

        onTokenError?.(errorObj);
        throw errorObj;
      }
    },
    [path, isPublic, onTokenFetched, onTokenError]
  );

  return {
    /** Function to pass to ChatKit's api.getClientSecret */
    getClientSecret,
    /** Clear the token cache (e.g., on logout) */
    clearCache: clearTokenCache,
    /** Check if a valid token is cached */
    hasValidToken,
  };
}

export default useChatKitToken;

